import { Button, Checkbox, Modal } from "@ths/ui";
import { useEffect, useRef, useState } from "react";

import {
  ReminderConsentFields,
  type ReminderOptions,
} from "./ReminderConsentFields";

/** A draft stays in the dialog until the guest explicitly saves it. */
export function ReminderSettings({
  promptOnMount = false,
  onPromptHandled,
  options,
  smsEnabled,
  emailEnabled,
  smsUnavailableReason,
  emailUnavailableReason,
  onSave,
}: {
  promptOnMount?: boolean;
  onPromptHandled?: () => void;
  options: ReminderOptions;
  smsEnabled: boolean;
  emailEnabled?: boolean;
  smsUnavailableReason?: string;
  emailUnavailableReason?: string;
  onSave: (sms: boolean, email: boolean) => Promise<void> | void;
}) {
  const [open, setOpen] = useState(promptOnMount);
  const [sms, setSms] = useState(smsEnabled);
  const [email, setEmail] = useState(emailEnabled ?? false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const triggerRef = useRef<HTMLButtonElement>(null);
  const wasOpen = useRef(false);
  const saveLock = useRef(false);
  const textsOn = smsEnabled && !smsUnavailableReason;
  const emailsOn = emailEnabled && !emailUnavailableReason;
  const enabled = textsOn || emailsOn;

  // Restore focus after Radix releases the dialog's focus trap.
  useEffect(() => {
    if (!open && wasOpen.current) triggerRef.current?.focus();
    wasOpen.current = open;
  }, [open]);

  const changeOpen = (value: boolean) => {
    if (saveLock.current) return;
    if (value) {
      setSms(smsEnabled && !smsUnavailableReason);
      setEmail(Boolean(emailEnabled && !emailUnavailableReason));
      setError("");
    }
    if (!value) onPromptHandled?.();
    setOpen(value);
  };

  return (
    <section
      className="flex items-center justify-between gap-4 rounded-2xl border border-on-navy/10 bg-on-navy/[0.03] p-4 text-on-navy"
      aria-label="Lodging updates"
    >
      <div className="space-y-1">
        <h2 className="text-sm font-medium">Lodging updates</h2>
        <p className="text-xs text-on-navy/70">
          {textsOn && emailsOn
            ? "Text and email reminders on"
            : textsOn
              ? "Text reminders on"
              : emailsOn
                ? "Email reminders on"
                : "Optional reminders for your stay"}
        </p>
      </div>
      <Button
        ref={triggerRef}
        type="button"
        variant="ghost"
        onClick={() => changeOpen(true)}
        className="min-h-11 shrink-0 rounded-xl border border-on-navy/20 text-on-navy hover:bg-on-navy/10 focus-visible:ring-on-navy"
      >
        {enabled ? "Manage" : "Set up"}
      </Button>
      <Modal
        open={open}
        onOpenChange={changeOpen}
        title="Lodging updates"
        description="Get lodging texts for current and future reservations and stays."
        className="max-h-[calc(100dvh-2rem)] overflow-y-auto rounded-2xl border border-on-navy/20 bg-navy-strong text-on-navy [&_button[aria-label=Close]]:text-on-navy/70 [&_h2]:text-on-navy [&_p.text-text-secondary]:text-on-navy/70"
        footer={
          <Button
            type="button"
            fullWidth
            variant="ghost"
            disabled={saving}
            className="min-h-12 rounded-xl border border-on-navy/25 bg-on-navy/10 text-on-navy hover:bg-on-navy/15 focus-visible:ring-on-navy"
            onClick={async () => {
              if (saveLock.current) return;
              saveLock.current = true;
              setSaving(true);
              setError("");
              try {
                await onSave(sms, email);
                onPromptHandled?.();
                setOpen(false);
              } catch (cause) {
                setError(
                  cause instanceof Error
                    ? cause.message
                    : "We could not save your preferences. Please try again.",
                );
              } finally {
                saveLock.current = false;
                setSaving(false);
              }
            }}
          >
            {saving ? "Saving…" : enabled ? "Save changes" : "Save"}
          </Button>
        }
      >
        <div className="space-y-5 py-3">
          {options.emailAvailable && emailEnabled !== undefined && (
            <section
              className="space-y-2 [&>label]:min-h-11 [&>label]:text-on-navy"
              aria-label="Email reminders"
            >
              <Checkbox
                checked={email}
                disabled={saving || Boolean(emailUnavailableReason)}
                onCheckedChange={setEmail}
                label="Email me"
                className="border-on-navy/50 bg-on-navy/5 focus-visible:ring-on-navy"
              />
              {emailUnavailableReason && (
                <p role="status" className="text-xs text-on-navy/70">
                  {emailUnavailableReason}
                </p>
              )}
            </section>
          )}
          {options.smsAvailable && (
            <div className="space-y-3">
              <ReminderConsentFields
                embedded
                options={options}
                checked={sms}
                disabled={saving || Boolean(smsUnavailableReason)}
                onCheckedChange={setSms}
              />
              {smsUnavailableReason && (
                <p role="status" className="text-sm text-on-navy/70">
                  {smsUnavailableReason}
                </p>
              )}
            </div>
          )}
          {error && (
            <p role="alert" className="text-sm text-error-surface">
              {error}
            </p>
          )}
        </div>
      </Modal>
    </section>
  );
}
